import { ref, readonly } from 'vue'

/**
 * 图片查看器状态管理 composable
 * 管理 Viewer 的打开/关闭状态、当前索引等
 */
export interface ViewerImage {
  ImageId: number
  Key: string
  Title: string | null
  Description: string | null
  BlurHash: string | null
  ExifData: string | null
  CreatedAt: string
}

// 单例状态（跨组件共享）
const isOpen = ref(false)
const currentIndex = ref(0)
const images = ref<ViewerImage[]>([])

// 记录打开时的滚动位置
let scrollPosition = 0

/**
 * 图片预加载缓存管理
 * 使用 Map 存储已加载的图片，避免重复请求
 */
const imageCache = new Map<string, HTMLImageElement>()
const loadingImages = new Set<string>()

/**
 * 预加载图片并缓存
 * @param url 图片 URL
 * @returns Promise<HTMLImageElement>
 */
export const preloadImage = (url: string): Promise<HTMLImageElement> => {
  // 已缓存直接返回
  if (imageCache.has(url)) {
    return Promise.resolve(imageCache.get(url)!)
  }

  // 正在加载中，返回等待
  if (loadingImages.has(url)) {
    return new Promise((resolve) => {
      const checkLoaded = setInterval(() => {
        if (imageCache.has(url)) {
          clearInterval(checkLoaded)
          resolve(imageCache.get(url)!)
        }
      }, 50)
    })
  }

  // 开始加载
  loadingImages.add(url)

  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      imageCache.set(url, img)
      loadingImages.delete(url)
      resolve(img)
    }
    img.onerror = (e) => {
      loadingImages.delete(url)
      reject(e)
    }
    img.src = url
  })
}

/**
 * 检查图片是否已缓存
 */
export const isImageCached = (url: string): boolean => {
  return imageCache.has(url)
}

/**
 * 清理缓存（可选，用于内存管理）
 * @param maxSize 最大缓存数量
 */
export const cleanupImageCache = (maxSize: number = 50) => {
  if (imageCache.size > maxSize) {
    const keysToDelete = Array.from(imageCache.keys()).slice(0, imageCache.size - maxSize)
    keysToDelete.forEach(key => imageCache.delete(key))
  }
}

export function useImageViewer() {
  /**
   * 打开图片查看器
   * @param imageList 图片列表
   * @param index 初始索引
   */
  const openViewer = (imageList: ViewerImage[], index: number = 0) => {
    images.value = imageList
    currentIndex.value = index

    // 记录当前滚动位置
    scrollPosition = window.scrollY

    isOpen.value = true
  }

  /**
   * 关闭图片查看器
   * @returns 当前查看的图片索引
   */
  const closeViewer = (): number => {
    const lastIndex = currentIndex.value
    isOpen.value = false
    return lastIndex
  }

  /**
   * 设置当前索引
   */
  const setCurrentIndex = (index: number) => {
    if (index >= 0 && index < images.value.length) {
      currentIndex.value = index
    }
  }

  /**
   * 获取保存的滚动位置
   */
  const getSavedScrollPosition = () => scrollPosition

  /**
   * 预加载相邻图片
   */
  const preloadAdjacent = () => {
    const current = currentIndex.value
    const list = images.value

    // 预加载前后各 2 张
    const indices = [
      current - 2,
      current - 1,
      current + 1,
      current + 2
    ].filter(i => i >= 0 && i < list.length)

    indices.forEach(i => {
      const item = list[i]
      if (item) {
        preloadImage(getFullImageUrl(item.Key))
      }
    })
  }

  return {
    // 只读状态
    isOpen: readonly(isOpen),
    currentIndex: readonly(currentIndex),
    images: readonly(images),

    // 方法
    openViewer,
    closeViewer,
    setCurrentIndex,
    getSavedScrollPosition,
    preloadAdjacent,

    // 缓存相关
    preloadImage,
    isImageCached,
    cleanupImageCache
  }
}

/**
 * 获取原图 URL
 */
export const getFullImageUrl = (key: string) =>
  // 有https开头就返回原来的
  key.startsWith('http') ? key : `https://image.lixiang.page/cdn-cgi/image/format=auto,quality=90/${key}`

/**
 * 获取缩略图 URL
 */
export const getThumbUrl = (key: string, width: number = 800) =>
  key.startsWith('http') ? key : `https://image.lixiang.page/cdn-cgi/image/format=auto,quality=40,width=${width}/${key}`

/**
 * 获取中等尺寸图片 URL（用于预览）
 */
export const getMediumImageUrl = (key: string) =>
  key.startsWith('http') ? key :
  `https://image.lixiang.page/cdn-cgi/image/format=auto,quality=75,width=1200/${key}`
